import { useState } from "react";
import { Link } from "react-router-dom";
import { Plus, BadgeCheck, Trash2, Building2, User } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { useLang } from "@/context/AppContext";
import { PageHead, PrimaryBtn } from "@/pages/app/Dashboard";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { useApi } from "@/lib/useApi";

const EMPTY = { kind: "person", name: "", headline: "", sector: "", location: "", verified: false };

const PrincipalForm = ({ onDone }) => {
  const { t } = useLang();
  const [f, setF] = useState(EMPTY);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const submit = async (e) => {
    e.preventDefault();
    try { await api.post("/principals", f); setF(EMPTY); onDone(); } catch (err) { toast.error(errMsg(err)); }
  };
  return (
    <form onSubmit={submit} className="space-y-4" data-testid="principal-form">
      <div className="flex gap-2">
        {["person", "company"].map((k) => (
          <button type="button" key={k} onClick={() => setF({ ...f, kind: k })} data-testid={`principal-kind-${k}`}
            className={`rounded-full border px-4 py-1.5 text-sm ${f.kind === k ? "border-emerald-400 bg-emerald-400/10 text-emerald-300" : "border-white/10 text-slate-400"}`}>{t(k)}</button>
        ))}
      </div>
      <Input required placeholder={t("name")} value={f.name} onChange={set("name")} data-testid="principal-name-input" />
      <Input placeholder={t("headline")} value={f.headline} onChange={set("headline")} data-testid="principal-headline-input" />
      <div className="grid grid-cols-2 gap-3">
        <Input placeholder={t("sector")} value={f.sector} onChange={set("sector")} data-testid="principal-sector-input" />
        <Input placeholder={t("location")} value={f.location} onChange={set("location")} data-testid="principal-location-input" />
      </div>
      <label className="flex items-center gap-3 text-sm text-slate-300"><Switch checked={f.verified} onCheckedChange={(v) => setF({ ...f, verified: v })} data-testid="principal-verified-switch" />{t("verified")}</label>
      <PrimaryBtn type="submit" data-testid="principal-submit-btn">{t("create")}</PrimaryBtn>
    </form>
  );
};

export default function Principals() {
  const { t } = useLang();
  const [open, setOpen] = useState(false);
  const { data: items, reload: load } = useApi("/principals", { initial: [] });
  const del = async (id) => { try { await api.delete(`/principals/${id}`); load(); } catch (e) { toast.error(errMsg(e)); } };

  return (
    <div>
      <PageHead title={t("app_principals")}>
        <PrimaryBtn onClick={() => setOpen(true)} data-testid="create-principal-btn"><Plus className="h-4 w-4" />{t("new_principal")}</PrimaryBtn>
      </PageHead>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="border-white/10 bg-[#111827]">
          <DialogHeader><DialogTitle className="font-display">{t("new_principal")}</DialogTitle><DialogDescription className="sr-only">AYDORIQ</DialogDescription></DialogHeader>
          <PrincipalForm onDone={() => { setOpen(false); load(); }} />
        </DialogContent>
      </Dialog>
      {!items.length && <p className="text-sm text-slate-500" data-testid="principals-empty">{t("empty")}</p>}
      <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
        {items.map((p) => {
          const Icon = p.kind === "company" ? Building2 : User;
          return (
            <div key={p.id} className="rounded-md border border-white/[0.08] bg-[#111827] p-6" data-testid={`principal-card-${p.id}`}>
              <div className="flex items-start justify-between">
                <div className="flex items-center gap-3">
                  <Icon className="h-5 w-5 text-cyan-300" strokeWidth={1.5} />
                  <div>
                    <Link to={`/app/profiles/${p.id}`} data-testid={`principal-profile-link-${p.id}`} className="flex items-center gap-1.5 font-display text-lg font-bold text-white hover:text-emerald-300">{p.name}{p.verified && <BadgeCheck className="h-4 w-4 text-emerald-400" />}</Link>
                    <p className="text-xs text-slate-400">{p.headline}</p>
                  </div>
                </div>
                <button onClick={() => del(p.id)} className="text-slate-500 hover:text-rose-400" data-testid={`principal-delete-${p.id}`}><Trash2 className="h-4 w-4" /></button>
              </div>
              <div className="mt-6 flex items-end justify-between">
                <p className="font-mono text-[11px] uppercase tracking-widest text-slate-500">{t(p.kind)} · {p.sector || "—"} · {p.location || "—"}</p>
                <div className="text-right">
                  <p className="font-mono text-[10px] uppercase tracking-widest text-slate-500">{t("trust")}</p>
                  <p className="font-display text-2xl font-bold text-emerald-300" data-testid={`principal-trust-${p.id}`}>{p.trust_score}</p>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
